import React, {RefObject} from "react";
import {V1ObjectMeta} from "@kubernetes/client-node";
import Label from "../../../Label/Label";
import Input from "../../../Input/Input";
import Description from "../../../Description/Description";
import {ResultError, Return} from "../../../../lib/result";
import {SectionProps} from "../../ItemDrawer";
import LabelsAnnotationsSection, {LabelsAnnotationsSectionProps} from "./LabelsAnnotationsSection";
import Query from "../../../Query/Query";
import client from "../../../../lib/client";
import {Resources} from "../../../../lib/resources";
import AutoComplete, {defaultAutoCompleteFilter} from "../../../AutoComplete/AutoComplete";
import {arr} from "../../../../lib/helpers/renderhelper";
import Section from "../../Section/Section";
import {DisplayNameObject} from "../../../../lib/helpers/sorthelper";
import SectionExpander from "../../SectionExpander/SectionExpander";

export function displayNameToID(displayName: string | undefined): string | undefined {
    return displayName?.toLowerCase().replace(/[^a-z0-9]/g, "-").replace(/--+/g, "-").replace(/(^[^a-z0-9])|([^a-z0-9]$)/, "")
}

export interface MetadataObject {
    metadata?: V1ObjectMeta;
}

interface MetadataState {
    name: string | undefined;
    nameChanged?: boolean;
    
    displayName?: string;
    displayNameChanged?: boolean;
    
    description?: string;
    descriptionChanged?: boolean;
    
    namespace?: string;
}

interface MetadataProps extends SectionProps, Omit<LabelsAnnotationsSectionProps, "metadata"> {
    type: string;
    cluster?: string;
    noMargin?: boolean;
    foldable?: boolean;
    defaultFolded?: boolean;
    forceNamespace?: boolean;
    namespaced?: boolean;
    namePlaceholder?: string;
    
    obj?: MetadataObject | DisplayNameObject;
    showDisplayName?: boolean;
    showDescription?: boolean;
}

export default class Metadata extends React.PureComponent<MetadataProps, MetadataState> {
    labelsSectionRef?: RefObject<LabelsAnnotationsSection>;
    state: MetadataState = {
        name: this.props.obj?.metadata?.name,
        nameChanged: false,
        
        displayName: (this.props.obj as DisplayNameObject)?.spec?.displayName,
        displayNameChanged: false,
        
        description: (this.props.obj as DisplayNameObject)?.spec?.description,
        descriptionChanged: false,
        
        namespace: this.props.obj?.metadata ? this.props.obj?.metadata?.namespace : undefined,
    };

    constructor(props: MetadataProps) {
        super(props);
        
        this.labelsSectionRef = React.createRef<LabelsAnnotationsSection>();
    }

    create = async (obj: MetadataObject | DisplayNameObject, generateName?: (metadata: V1ObjectMeta) => Promise<ResultError>): Promise<ResultError> => {
        if (!obj.metadata) {
            obj.metadata = {};
        }
        
        if (!this.state.name) {
            if (generateName) {
                const result = await generateName(obj.metadata);
                if (result.err) {
                    return result;
                }
            } else {
                obj.metadata.generateName = this.props.type.toLocaleLowerCase().replaceAll(" ", "-")+"-";
            }
        } else {
            obj.metadata.name = this.state.name;
        }
        if (this.props.namespaced) {
            obj.metadata.namespace = this.state.namespace;
          if (!this.state.namespace) {
            return Return.Failed("Please enter a namespace");
          }
        }
        if (this.state.displayName) {
            if (!(obj as DisplayNameObject).spec) {
                (obj as DisplayNameObject).spec = {};
            }

            (obj as DisplayNameObject)!.spec!.displayName = this.state.displayName
        }
        if (this.state.description) {
            if (!(obj as DisplayNameObject).spec) {
                (obj as DisplayNameObject).spec = {};
            }

            (obj as DisplayNameObject)!.spec!.description = this.state.description
        }

        return this.labelsSectionRef!.current!.create(obj.metadata);
    };

    update = (obj: MetadataObject | DisplayNameObject): ResultError => {
        if (this.state.displayNameChanged) {
            if (!(obj as DisplayNameObject).spec) {
                (obj as DisplayNameObject).spec = {};
            }

            (obj as DisplayNameObject)!.spec!.displayName = this.state.displayName;
        }
        if (this.state.descriptionChanged) {
            if (!(obj as DisplayNameObject).spec) {
                (obj as DisplayNameObject).spec = {};
            }

            (obj as DisplayNameObject)!.spec!.description = this.state.description;
        }
        
        return this.labelsSectionRef!.current!.update(obj.metadata!);
    };

    batch = (metadatas: Array<MetadataObject | DisplayNameObject>): ResultError => {
        return this.labelsSectionRef!.current!.batch(metadatas.map(m => m.metadata!));
    };

    renderNamespace() {
        if (!this.props.namespaced) {
            return null;
        }
        if (this.props.mode === "update" || this.props.forceNamespace) {
            return <div>
                <Label>Namespace</Label>
                <Input readOnly={true} value={this.state.namespace} />
            </div>;
        }
        
        if (!this.props.cluster) {
            return <div>
                <Label>Namespace</Label>
                <Input placeholder={"my-namespace"}
                       value={this.state.namespace} 
                       onChange={(val) => this.setState({namespace: val.target.value})} />
                <Description>Enter a namespace to create this {this.props.type} in.</Description>
            </div>
        }
        return <div>
          <Label>Namespace</Label>
          <Query query={async () => await client.cluster(this.props.cluster!, Resources.ClusterV1Space).List()} refetch={[this.props.cluster]}>
              {
                  result => {
                      return <AutoComplete placeholder={"my-namespace"}
                                           value={this.state.namespace}
                                           filterOption={defaultAutoCompleteFilter}
                                           showArrow={true}
                                           allowClear={true}
                                           onChange={(val) => this.setState({namespace: val})}
                                           options={arr(result.data?.items).map(space => ({label: space.metadata?.name!, value: space.metadata?.name!}))} />
                  }
              }
          </Query>
          <Description>Choose a namespace to create this {this.props.type} in.</Description>
        </div>
    }
    
    renderName() {
        const nameDiv = <div>
            <Label>Kubernetes Name (ID)</Label>
            <Input readOnly={this.props.mode === "update"} 
                   placeholder={this.props.namePlaceholder || "Leave empty for auto-generated name"} 
                   value={this.props.obj?.metadata?.name || this.state.name} 
                   onChange={(e) => this.setState({name: e.target.value, nameChanged: true})} />
            <Description>{this.props.mode === "update" ? "The underlying k8s resource cannot be renamed" : "Leave empty to auto-generate a name"}</Description>
        </div>;
        
        if (this.props.showDisplayName) {
            return <div className={"row"}>
                <div>
                    <Label>Display Name</Label>
                    <Input resetable={this.props.mode === "update"}
                           placeholder={this.props.namePlaceholder || "My "+this.props.type}
                           value={this.state.displayName}
                           onChangedStatus={changed => this.setState({displayNameChanged: changed})}
                           onChange={(e) => {
                                if (this.props.mode === "create" && this.state.nameChanged === false) {
                                    this.setState({displayName: e.target.value, name: displayNameToID(e.target.value)})
                                } else {
                                    this.setState({displayName: e.target.value})
                                }
                            }} />
                    <Description>The name that will displayed in the UI for this object.</Description>
                </div>
                {nameDiv}
            </div>
        }
        
        return nameDiv;
    }

    render() {
        const namespaceDiv = this.renderNamespace()
        return <Section title={`Metadata`} noMargin={this.props.noMargin} foldable={this.props.foldable} defaultFolded={this.props.defaultFolded}>
            {
                this.props.mode !== "batch" && this.renderName()
            }
            {
                this.props.mode !== "batch" && this.props.showDescription && <SectionExpander name={"Description"}>
                    <Label>Description</Label>
                    <Input resetable={this.props.mode === "update"}
                           onChangedStatus={changed => this.setState({descriptionChanged: changed})}
                           placeholder={`The ${this.props.type} is doing...`}
                           value={this.state.description}
                           onChange={e => this.setState({description: e.target.value})} />
                    <Description>A human readable description what the {this.props.type} is used for. Markdown can be used here.</Description>
                </SectionExpander>
            }
            <LabelsAnnotationsSection {...this.props} filterAnnotations={["loft.sh/apply", ...arr(this.props.filterAnnotations)]} name={`Labels & Annotations`} metadata={this.props.obj?.metadata} ref={this.labelsSectionRef} />
            {
                this.props.mode !== "batch" && namespaceDiv && <SectionExpander name={this.props.name || "Namespace"}>
                    {namespaceDiv}
                </SectionExpander>
            }
        </Section>
    }
}